/**
 * Optica Store Locator — boots one Google Map per .opt-store-locator container,
 * draws a branded pin per branch, and wires the list, search box and country
 * chips to the map. Ported from the optica-store-locator WP plugin.
 *
 * Markers are CLASSIC google.maps.Marker with an inline SVG pin:
 * AdvancedMarkerElement renders nothing without a registered mapId — classic
 * markers need no mapId and let us colour the pins (resting vs selected).
 * Pin colour follows each container's --osl-accent CSS variable.
 *
 * Coordinates arrive as strings from Liquid, so they're parsed with parseFloat.
 * Customer-facing strings (count words, directions) come from data-* attributes
 * so Shopify/Transcy translation can localise them.
 */
(function () {
  'use strict';

  var bootTries = 0;
  var FALLBACK = '#1B9CD8';

  function pinIcon(color, scale) {
    var svg =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="' + 34 * scale + '" height="' + 34 * scale + '">' +
      '<path fill="' + color + '" stroke="#ffffff" stroke-width="1.2" d="M12 1.5c-4.7 0-8.5 3.8-8.5 8.5 0 6.4 8.5 12.5 8.5 12.5s8.5-6.1 8.5-12.5c0-4.7-3.8-8.5-8.5-8.5z"/>' +
      '<circle cx="12" cy="10" r="3.2" fill="#ffffff"/></svg>';
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
      scaledSize: new google.maps.Size(34 * scale, 34 * scale),
      anchor: new google.maps.Point(17 * scale, 34 * scale),
    };
  }

  var MAP_STYLES = [
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#CFE8F7' }] },
    { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#f8f9fa' }] },
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'road.arterial', elementType: 'geometry.stroke', stylers: [{ color: '#e5e7eb' }] },
    { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  ];

  function initAll() {
    document.querySelectorAll('.opt-store-locator').forEach(function (c) {
      try {
        initLocator(c);
      } catch (e) {
        c.dataset.optInit = '';
        if (window.console && console.error) console.error('store-locator init failed', e);
      }
    });
  }

  function boot() {
    if (!document.querySelector('.opt-store-locator')) return;
    var g = window.google;

    if (g && g.maps && typeof g.maps.Map === 'function') { initAll(); return; }

    if (g && g.maps && typeof g.maps.importLibrary === 'function') {
      Promise.all([
        g.maps.importLibrary('maps'),
        g.maps.importLibrary('marker').catch(function () {}),
      ]).then(initAll).catch(function () {
        if (bootTries++ < 100) window.setTimeout(boot, 200);
      });
      return;
    }

    if (bootTries++ < 100) window.setTimeout(boot, 200);
  }

  function darken(hex) {
    var m = /^#?([0-9a-f]{6})$/i.exec((hex || '').trim());
    if (!m) return '#0E6BAD';
    var n = parseInt(m[1], 16);
    var r = Math.max(0, ((n >> 16) & 255) - 40);
    var gg = Math.max(0, ((n >> 8) & 255) - 40);
    var b = Math.max(0, (n & 255) - 40);
    return '#' + ((1 << 24) + (r << 16) + (gg << 8) + b).toString(16).slice(1);
  }

  function initLocator(container) {
    if (container.dataset.optInit === '1') return;
    container.dataset.optInit = '1';

    var accent = (getComputedStyle(container).getPropertyValue('--osl-accent') || '').trim() || FALLBACK;
    var accentActive = darken(accent);

    var wordOne = container.dataset.countOne || 'branch';
    var wordMany = container.dataset.countMany || 'branches';
    var dirLabel = container.dataset.directionsLabel || 'Directions';

    const dataEl = container.querySelector('[data-opt-storeloc-data]');
    const mapEl = container.querySelector('[data-opt-storeloc-map]');
    const listEl = container.querySelector('.opt-store-locator__list');
    const rows = Array.prototype.slice.call(container.querySelectorAll('.opt-store-row'));
    if (!dataEl || !mapEl) return;

    let data = {};
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
    const markers = (data.markers || []).map(function (m) {
      return {
        id: String(m.id),
        title: m.title,
        city: m.city,
        mapUrl: m.mapUrl,
        lat: parseFloat(m.lat),
        lng: parseFloat(m.lng),
      };
    }).filter(function (m) { return !isNaN(m.lat) && !isNaN(m.lng); });
    if (!markers.length) return;

    const map = new google.maps.Map(mapEl, {
      center: { lat: markers[0].lat, lng: markers[0].lng },
      zoom: 11,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      clickableIcons: false,
      styles: MAP_STYLES,
    });

    const byId = {};
    let activeId = null;
    let infoWindow = null;

    markers.forEach(function (m) {
      const position = { lat: m.lat, lng: m.lng };
      const marker = new google.maps.Marker({
        position: position,
        map: map,
        title: m.title,
        icon: pinIcon(accent, 1),
      });
      marker.addListener('click', function () { focusBranch(m.id, true); });
      byId[m.id] = { marker: marker, position: position, meta: m };
    });

    function visibleEntries() {
      return rows
        .filter(function (r) { return !r.hidden && byId[r.dataset.branchId]; })
        .map(function (r) { return byId[r.dataset.branchId]; });
    }

    function frameVisible() {
      const entries = visibleEntries();
      if (!entries.length) return;
      if (entries.length === 1) {
        map.panTo(entries[0].position);
        map.setZoom(15);
        return;
      }
      const bounds = new google.maps.LatLngBounds();
      entries.forEach(function (e) { bounds.extend(e.position); });
      map.fitBounds(bounds, 48);
    }

    frameVisible();

    function focusBranch(branchId, scrollList) {
      const entry = byId[branchId];
      if (!entry) return;

      if (activeId && byId[activeId]) {
        byId[activeId].marker.setIcon(pinIcon(accent, 1));
        byId[activeId].marker.setZIndex(1);
      }
      activeId = branchId;
      entry.marker.setIcon(pinIcon(accentActive, 1.25));
      entry.marker.setZIndex(99);

      map.panTo(entry.position);
      map.setZoom(16);

      rows.forEach(function (r) {
        r.classList.toggle('opt-store-row--active', String(r.dataset.branchId) === String(branchId));
      });

      if (scrollList) {
        const row = container.querySelector('.opt-store-row[data-branch-id="' + branchId + '"]');
        if (row && listEl) row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }

      if (infoWindow) infoWindow.close();
      const meta = entry.meta;
      const dirLink = meta.mapUrl
        ? '<a href="' + escAttr(meta.mapUrl) + '" target="_blank" rel="noopener" style="display:inline-block;margin-top:6px;color:' + escAttr(accent) + ';font-weight:600;text-decoration:none">' + escHtml(dirLabel) + ' &rarr;</a>'
        : '';
      infoWindow = new google.maps.InfoWindow({
        content:
          '<div style="font-family:inherit;max-width:220px;padding:2px 2px 4px">' +
          '<div style="font-size:13px;font-weight:600;color:#1a2332;line-height:1.35">' + escHtml(meta.title) + '</div>' +
          (meta.city ? '<div style="font-size:11.5px;color:#6b7280;margin-top:2px">' + escHtml(meta.city) + '</div>' : '') +
          dirLink + '</div>',
      });
      infoWindow.open({ map: map, anchor: entry.marker });
    }

    rows.forEach(function (row) {
      row.addEventListener('click', function (e) {
        if (e.target.closest('a')) return;
        const id = row.dataset.branchId;
        if (id) focusBranch(id, false);
      });
    });

    const searchBox = container.querySelector('[data-opt-storeloc-search]');
    const chips = Array.prototype.slice.call(container.querySelectorAll('.opt-store-locator__chip'));
    const countEl = container.querySelector('[data-opt-storeloc-count]');
    const emptyEl = container.querySelector('[data-opt-storeloc-empty]');
    let country = '';

    function applyFilters() {
      const needle = (searchBox ? searchBox.value : '').trim().toLowerCase();
      let shown = 0;
      rows.forEach(function (r) {
        const matchCountry = !country || (r.dataset.country || '') === country;
        const matchSearch = !needle || (r.dataset.search || '').indexOf(needle) !== -1;
        const show = matchCountry && matchSearch;
        r.hidden = !show;
        const entry = byId[r.dataset.branchId];
        if (entry) entry.marker.setVisible(show);
        if (show) shown++;
      });
      if (countEl) countEl.textContent = shown + ' ' + (shown === 1 ? wordOne : wordMany);
      if (emptyEl) emptyEl.hidden = shown !== 0;
      if (infoWindow) infoWindow.close();
      if (activeId && byId[activeId]) {
        byId[activeId].marker.setIcon(pinIcon(accent, 1));
        rows.forEach(function (r) { r.classList.remove('opt-store-row--active'); });
        activeId = null;
      }
      frameVisible();
    }

    if (searchBox) {
      let t;
      searchBox.addEventListener('input', function () {
        clearTimeout(t);
        t = setTimeout(applyFilters, 180);
      });
    }
    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        country = chip.dataset.country || '';
        chips.forEach(function (c) { c.classList.toggle('opt-store-locator__chip--active', c === chip); });
        applyFilters();
      });
    });

    applyFilters();
  }

  function escHtml(s) {
    const d = document.createElement('div');
    d.textContent = s == null ? '' : String(s);
    return d.innerHTML;
  }
  function escAttr(s) { return escHtml(s).replace(/"/g, '&quot;'); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
